import { observable, action, makeObservable } from 'mobx';

import _ from 'lodash';

import agent from '../agent';

import { payrollIntegrationOptions } from '../../utils/constants';

let projectAddressThrottleTimer = null;

class ProjectStore {
    constructor() {
        makeObservable(this, {
            currentList: observable,
            projectCounts: observable,
            projectTotalHours: observable,
            currentEntity: observable,
            updatingErrors: observable,
            updating: observable,
            loading: observable,
            requestParams: observable,
            deleteSuccess: observable,
            currentProjectsLookup: observable,
            loadingLookup: observable,
            gpsNowLoading: observable,
            filters: observable,
            appliedFilters: observable,
            projectMembersLookup : observable,
            getIntegrationProjectsCount : observable,
            setFilter: action,
            onFilter: action,
            lastListLoadTime: observable,
            resetLastListLoadTime: action,
            loadList: action,
            returnDefaultNew: action,
            load: action,
            getProjectMembersIds: action,
            save: action,
            remove: action,
            loadLookup: action,
            performRecalcGpsForProject: action,
            recalcGpsForProject: action,
            saveIntoIntegration: action,
            saveProjectFromIntegration: action,
            getProjectPmShiftPermissions: action,
            getProjectPmList: action,
            setProjectPmShiftPermissions: action,
            sendInviteToContactPerson: action,
            prependProjectToLookup: action,
            projectMemberLookup: action,
            getProjectTotals: action,
            getProjectTotalHours: action,
            saveProjectMembers: action,
            getProjectMembers: action,
            getProjectMembersCount : action,
            deleteProjectMember : action,
            lastInteractedId: observable,
            setLastInteractedId: action,
            clearLastInteractedId: action,
            pendingPmRulesChange: observable,
            setPendingPmRulesChange: action,
        });
    }

    lastInteractedId = null;
    pendingPmRulesChange = null;

    setLastInteractedId(id) {
        this.lastInteractedId = id ?? null;
    }

    clearLastInteractedId() {
        this.lastInteractedId = null;
    }

    setPendingPmRulesChange(value) {
        this.pendingPmRulesChange = value ?? null;
    }

    currentList = [];
    projectCounts = {
        allProjects: 0,
        activeProjects: 0,
        inactiveProjects: 0
    };
    projectTotalHours = {};
    currentEntity = {};
    projectMembersLookup = {
        rows : [],
        pages : 0
    }
    updatingErrors = null;
    updating = false;
    loading = false;
    requestParams = null;
    deleteSuccess = false;
    currentProjectsLookup = {
        rows: [],
        pages: 0
    };
    loadingLookup = false;
    gpsNowLoading = false;
    filters = {
        status: 'active',
        name: '',
    };
    appliedFilters = {
        status: 'active',
        name: '',
    };


    setFilter(name, value) {
        const filters = Object.assign({}, this.filters);
        filters[name] = value;
        this.filters = filters;
    }

    onFilter() {
        this.appliedFilters = Object.assign({}, this.filters);
    }

    lastListLoadTime = null;

    resetLastListLoadTime() {
        this.lastListLoadTime = new Date();
    }

    loadList(params) {
        return agent.Projects.list(params)
            .then(
                action(list => {
                    this.requestParams = params;
                    list.time = new Date();
                    this.lastListLoadTime = list.time;
                    this.currentList = list;
                    return list;
                })
            )
            .catch(
                action(err => {
                    throw err;
                })
            );
    }

    returnDefaultNew(params) {
        this.currentEntity = { project: { name: null, data: {} } };
        this.loading = false;
    }

    load(id) {
        this.loading = true;
        return agent.Projects.load(id)
            .then(
                action(response => {
                    if (!response.project.data) response.project.data = {};
                    this.currentEntity = response;
                    this.loading = false;
                    return response;
                })
            )
            .catch(
                action(err => {
                    this.loading = false;
                    throw err;
                })
            );
    }

    getProjectMembersIds(id) {
        this.loading = true;
        return agent.Projects.getProjectMembersIds(id)
            .then(
                action(response => {
                    this.loading = false;
                    return response;
                })
            )
            .catch(
                action(err => {
                    this.loading = false;
                    throw err;
                })
            );
    }

    save(values, isAdd) {
        this.updating = true;
        return agent.Projects.save(values, isAdd)
            .then(
                action(user => {
                    this.currentEntity = user;
                    return user;
                })
            )
            .catch(
                action(err => {
                    this.updating = false;
                    this.updatingErrors = err.response && err.response.body && err.response.body.errors;
                    throw err;
                })
            );
    }

    async remove(id, confirm) {
        const res = await agent.Projects.remove(id, confirm);
        if (confirm) {
            this.deleteSuccess = true;
            return 1;
        }
        return res;
    }

    loadLookup(name = undefined, userId = undefined, page = 0, fromShift) {
        this.loadingLookup = true;
        const params = fromShift ? { name, userId, page } : { name, userId, page }
        const apiFn = fromShift ? agent.Projects.lookupPmByName : agent.Projects.lookupByName;
        return apiFn(params)
            .then(
                action(list => {
                    if (page === 0) {
                        this.currentProjectsLookup.rows = list.rows
                    } else {
                        this.currentProjectsLookup.rows = [...this.currentProjectsLookup.rows, ...list.rows];
                    }
                    this.currentProjectsLookup.pages = list.pages
                    this.loadingLookup = false;
                    return list.rows;
                })
            )
            .catch(
                action(err => {
                    this.loadingLookup = false;
                    throw err;
                })
            )
    }

    async performRecalcGpsForProject(newAddress) {
        this.gpsNowLoading = true;
        this.currentEntity.project.data.addressCache = newAddress;
        const tsk = _.cloneDeep(this.currentEntity.project);
        try {
            const data = await agent.Tasks.gpsAddress(newAddress, null, false);
            tsk.gps_data = data.result;
            tsk.data.addressCache = newAddress;
            this.currentEntity = { project: tsk };
            this.gpsNowLoading = false;
        } catch (e) {
            this.gpsNowLoading = false;
            throw e;
        }
    }

    async recalcGpsForProject(changes) {
        const entityToAddressString = e => {
            return `${e.address}, ${e.post_place}, ${e.post_number}`;
        };
        if (!changes.address || !changes.post_place) {
            return changes;
        }

        const newAddress = entityToAddressString(changes);

        if (newAddress === this.currentEntity.project.data.addressCache) return changes;

        projectAddressThrottleTimer = new Date().getTime();
        setTimeout(() => {
            // throttle
            if (new Date().getTime() - projectAddressThrottleTimer < 1000) {
                return;
            }
            if (this.currentEntity.project.data.addressCache === newAddress) {
                return;
            }
            this.performRecalcGpsForProject(newAddress);
        }, 1000);
        return changes;
    }

    saveIntoIntegration(values) {
        return agent.Projects.saveIntoIntegration(values)
            .then(
                action(project => {
                    return project;
                })
            )
            .catch(
                action(err => {
                    this.updating = false;
                    this.updatingErrors = err.response && err.response.body && err.response.body.errors;
                    throw err;
                })
            );
    }

    getAllProjectFromIntegration(params) {
        return agent.Projects.getAllProjectFromIntegration(params).then(
            action(projectlist => {
                return projectlist
            })
        ).catch(
            action(err => {
                this.updating = false;
                this.updatingErrors = err.response && err.response.body && err.response.body.errors;
                throw err;
            })
        );
    }

    getProjectsTaskFromIntegration(params) {
        return agent.Projects.getProjectsTaskFromIntegration(params).then(
            action(tasklist => {
                return tasklist
            })
        ).catch(
            action(err => {
                this.updating = false;
                this.updatingErrors = err.response && err.response.body && err.response.body.errors;
                throw err;
            })
        )
    }

    saveProjectFromIntegration(data) {
        if (data && !data.product) return;
        const endPoint = data.product === payrollIntegrationOptions.TRIPLETEX ? 'saveProjectFromIntegration' : 'saveProjectFromIntegrationXledger';
        return agent.Projects[endPoint](data).then(
            action(res => {
                return res
            })
        ).catch(
            action(err => {
                this.updating = false;
                this.updatingErrors = err.response && err.response.body && err.response.body.errors;
                throw err;
            })
        )
    }

    getProjectPmShiftPermissions(user_id) {
        return agent.Projects.getProjectPmShiftPermissions(user_id).then(
            action(res => {
                return res;
            })
        ).catch(
            action(err => {
                throw err;
            })
        )
    }
    getProjectPmList(projectId) {
        return agent.Projects.getProjectPmList(projectId).then(
            action(res => res)
        ).catch(
            action(err => { throw err; })
        );
    }

    setProjectPmShiftPermissions(data) {
        return agent.Projects.setProjectPmShiftPermissions(data).then(
            action(res => {
                return res;
            })
        ).catch(
            action(err => {
                throw err;
            })
        )
    }

    sendInviteToContactPerson(data) {
        return agent.Projects.sendInviteToContactPerson(data).then(
            action(res => {
                return res;
            })
        ).catch(
            action(err => {
                throw err;
            })
        )
    }

    prependProjectToLookup(project) {
        if (!this.currentProjectsLookup.rows) {
            this.currentProjectsLookup.rows = [];
        }

        const exists = this.currentProjectsLookup?.rows.find(p => p.id === project.id);
        if (!exists) {
            this.currentProjectsLookup.rows = [project, ...this.currentProjectsLookup.rows];
        }
    }

    getProjectTotals() {
        return agent.Projects.getProjectTotals()
            .then(
                action(response => {
                    const { all_projects, active_projects, inactive_projects } = response;
                    this.projectCounts.allProjects = all_projects;
                    this.projectCounts.activeProjects = active_projects;
                    this.projectCounts.inactiveProjects = inactive_projects;
                    return response;
                })
            )
            .catch(
                action(err => {
                    throw err;
                })
            );
    }

    getProjectTotalHours(id, params) {
        return agent.Projects.getProjectTotalHours(id, params)
            .then(
                action(response => {
                    this.projectTotalHours[id] = response ? response : 0;
                    return true;
                })
            )
             .catch(
                action(err => {
                    throw err;
                })
            );
    }

    projectMemberLookup(projectId, isNotPagination = false, page = 0, name = '') {
        this.loadingLookup = true;
        const params = { isNotPagination : isNotPagination ? true : '' , page , name };
        return agent.Projects.projectMemberLookup(projectId, params)
            .then(
                action(list => {
                    if(page === 0 ){
                        this.projectMembersLookup.rows = list.rows
                    } else {
                        this.projectMembersLookup.rows = [...this.projectMembersLookup.rows, ...list.rows];
                    }
                    this.projectMembersLookup.pages = list.pages;
                    this.loadingLookup = false;
                    return list;
                })
            ).catch(
                action(err => {
                    throw err;
                })
            );
    }

    saveProjectMembers(values) {
        return agent.Projects.saveProjectMembers(values)
            .then(
                action(list => {
                    return list;
                })
            ).catch(
                action(err => {
                    throw err;
                })
            )
    }

    getProjectMembers(projectId, options = {}) {
        if (!projectId) return;
        const { name = '', assigned = 'all', page = 0, pagesize, isNotPagination = false, pmMembers = [], groupCodes = '' } = options;
        const params = { projectId, name, assigned, page, pagesize, isNotPagination: isNotPagination ? true : '', pmMembers, groupCodes };
        return agent.Projects.getProjectMembers(params)
            .then(
                action(list => {
                    return list;
                })
            ).catch(
                action(err => {
                    throw err;
                })
            )
    }

    getProjectMembersCount(projectId, options = {}) {
        if (!projectId) return;
        const { pmMembers = []} = options;
        const params = { projectId, pmMembers };
        return agent.Projects.getProjectMembersCount(params)
           .then(
                action(list => {
                    return list;
                })
            ).catch(
                action(err => {
                    throw err;
                })
            )
    }

    deleteProjectMember(projectId, userId) {
        if (!projectId || !userId) return;
        return agent.Projects.deleteProjectMember({ projectId, userId })
             .then(
                action(list => {
                    return list;
                })
            ).catch(
                action(err => {
                    throw err;
                })
            )
    }

    getIntegrationProjectsCount(filter) {
        if(!filter) return;
        return agent.Projects.getIntegrationProjectsCount(filter)
             .then(
                action(list => {
                    return list;
                })
            ).catch(
                action(err => {
                    throw err;
                })
            )
    }
}

const _ProjectStore = new ProjectStore();
export default _ProjectStore;
